import Log from '@/classes/debug/Log'
import certificateApi from '@/api/certificate/certificateApi'
import FormData from '@/classes/form/FormData'
import FormDescriptor from '@/classes/form/FormDescriptor'
import { Certificate, CertificateState, ServiceCodes } from '@/classes/certificate/Certificate'
import { findItem, getFilter, downloadXlsFromResponse } from '@/store/utility_functions'

const DEFAULT_SORT = 'certificate_number'

const state = {
  certificates: [],
  certificateFormDescriptor: [],
  certificateFormData: [],
  revokeCertificateFormDescriptor: [],
  revokeCertificateFormData: [],
  suspendCertificateFormDescriptor: [],
  suspendCertificateFormData: []
}
const getters = {
  getCertificateForId: (state) => (certificateId) => {
    return findItem(state.certificates, certificateId)
  }
}
const mutations = {
  setStateCertificateFormDescriptors: (state, data) => {
    state.certificateFormDescriptor = FormDescriptor.asFormDescriptors(data)
  },
  setStateCertificateFormDatas: (state, data) => {
    state.certificateFormData = FormData.initiateFormDatas(state.certificateFormDescriptor)
    FormData.loadInitialFormDatas(data, state.certificateFormData)
    FormDescriptor.checkFormDescriptorsDependencies(state.certificateFormDescriptor, state.certificateFormData)
  },
  setStateRevokeCertificateFormDescriptors: (state, data) => {
    state.revokeCertificateFormDescriptor = FormDescriptor.asFormDescriptors(data)
  },
  setStateSuspendCertificateFormDescriptors: (state, data) => {
    state.suspendCertificateFormDescriptor = FormDescriptor.asFormDescriptors(data)
  },
  setStateRevokeCertificateFormDatas: (state, data) => {
    state.revokeCertificateFormData = FormData.initiateFormDatas(state.revokeCertificateFormDescriptor)
    FormData.loadInitialFormDatas(data, state.revokeCertificateFormData)
    FormDescriptor.checkFormDescriptorsDependencies(state.revokeCertificateFormDescriptor, state.revokeCertificateFormData)
  },
  setStateSuspendCertificateFormDatas: (state, data) => {
    state.suspendCertificateFormData = FormData.initiateFormDatas(state.suspendCertificateFormDescriptor)
    FormData.loadInitialFormDatas(data, state.suspendCertificateFormData)
    FormDescriptor.checkFormDescriptorsDependencies(state.suspendCertificateFormDescriptor, state.suspendCertificateFormData)
  },
  setStateCertificates: (state, certificates) => {
    state.certificates = certificates
  },
  addToStateCertificates: (state, certificate) => {
    state.certificates.push(certificate)
  },
  updateStateCertificates: (state, data) => {
    data.current.updateCertificate(data.updated)
  }
}
const actions = {
  getCertificates ({ commit }, request) {
    request.ordering = request.ordering || DEFAULT_SORT
    let filter = getFilter(request)
    return certificateApi.getCertificates(filter).then((response) => {
      // TODO: should remove when backend is returning the correct structure
      for (let result of response.data.results) {
        result.reportings = result.reportings.map((report) => {
          if (report) {
            report.document = { title: 'Filename' + report.id, files: report.files || [] }
            return report
          }
        })
      }
      let certificates = Certificate.asCertificates(response.data.results)
      commit('setStateCertificates', certificates)
      return response.data
    }).catch((err) => {
      commit('setStateCertificates', [])
      throw err
    })
  },
  getCertificatesUniqueCertNumber ({ commit }, request) {
    request.ordering = request.ordering || DEFAULT_SORT
    let filter = getFilter(request)
    return certificateApi.getCertificatesUniqueCertNumber(filter).then((response) => {
      // TODO: should remove when backend is returning the correct structure
      for (let result of response.data.results) {
        result.reportings = result.reportings.map((report) => {
          if (report) {
            report.document = { title: 'Filename' + report.id, files: report.files || [] }
            return report
          }
        })
      }
      let certificates = Certificate.asCertificates(response.data.results)
      commit('setStateCertificates', certificates)
      return response.data
    }).catch((err) => {
      commit('setStateCertificates', [])
      throw err
    })
  },
  getSuspendedCertificates ({ commit }, request) {
    request.state = CertificateState.SUSPENDED
    return actions.getCertificates({ commit }, request)
  },
  getAdministratorSuspendedCertificates ({ commit }, request) {
    request.suspension_registered = 'false'
    return actions.getSuspendedCertificates({ commit }, request)
  },
  getPOPCertificates ({ commit }, request) {
    request.service = ServiceCodes.POP
    return actions.getCertificates({ commit }, request)
  },
  setCertificate ({ commit, state }, certificateId) {
    return certificateApi.getCertificate(certificateId).then((response) => {
      // TODO: should remove when backend is returning the correct structure
      response.data.reportings = response.data.reportings.map((report) => {
        if (report) {
          report.document = { title: 'Filename' + report.id, files: report.files || [] }
          return report
        }
      })
      let certificate = Certificate.asCertificate(response.data)
      let existingItem = findItem(state.certificates, certificate.id)
      if (existingItem) {
        commit('updateStateCertificates', {
          current: existingItem,
          updated: certificate
        })
      } else {
        commit('addToStateCertificates', certificate)
      }
      return certificate
    }).catch((err) => {
      throw err
    })
  },
  setupCertificateForm ({ commit }, requestData) {
    let request = requestData.certificateId ? certificateApi.getCertificateForm(requestData.certificateId) : certificateApi.getCertificateFormLayout(requestData.caseId)
    return request.then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateCertificateFormDescriptors', [response.data])
      commit('setStateCertificateFormDatas', formData)
    }).catch((err) => {
      throw err
    })
  },
  postCertificateForm ({ commit }, requestData) {
    return certificateApi.postCertificateForm(requestData.data.caseId, requestData.state, requestData.serializedData).then((response) => {
      Log.debug('Posted Form Data for Certificate Form ' + requestData.data.caseId)
      return Certificate.asCertificate(response.data)
    }).catch((err) => {
      throw err
    })
  },
  patchCertificateForm ({ commit }, requestData) {
    return certificateApi.patchCertificateForm(requestData.data.certificateId, requestData.state, requestData.serializedData).then((response) => {
      Log.debug('Patched Form Data for Certificate Form ' + requestData.data.certificateId)
      return Certificate.asCertificate(response.data)
    }).catch((err) => {
      throw err
    })
  },
  saveCertificateFormAndPublish ({ dispatch }, requestData) {
    requestData.state = CertificateState.PUBLISH
    return dispatch('saveCertificateForm', requestData)
  },
  saveCertificateFormAsDraft ({ dispatch }, requestData) {
    requestData.state = CertificateState.DRAFT
    return dispatch('saveCertificateForm', requestData)
  },
  saveCertificateForm ({ dispatch }, requestData) {
    return requestData.data.certificateId ? dispatch('patchCertificateForm', requestData) : dispatch('postCertificateForm', requestData)
  },
  setupRevokeCertificateForm ({ commit }, certificateId) {
    return certificateApi.getRevokeCertificateForm(certificateId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateRevokeCertificateFormDescriptors', [response.data])
      commit('setStateRevokeCertificateFormDatas', formData)
    }).catch((err) => {
      throw err
    })
  },
  setupSuspendCertificateForm ({ commit }, certificateId) {
    return certificateApi.getSuspendCertificateForm(certificateId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateSuspendCertificateFormDescriptors', [response.data])
      commit('setStateSuspendCertificateFormDatas', formData)
    }).catch((err) => {
      throw err
    })
  },
  setupSuspensionUpdateForm ({ commit }, certificateId) {
    return certificateApi.getSuspensionUpdateForm(certificateId).then((response) => {
      let formData = response.data.form_data ? [response.data.form_data] : []
      commit('setStateSuspendCertificateFormDescriptors', [response.data])
      commit('setStateSuspendCertificateFormDatas', formData)
    }).catch((err) => {
      throw err
    })
  },
  setupUnsuspendCertificate ({ commit }, requestData) {
    return certificateApi.putUnsuspendCertificate(requestData.certificate_id)
      .then((response) => {
        return response
      })
  },
  suspensionRegisteredAction ({ commit }, certificateId) {
    return certificateApi.putSuspensionRegistered(certificateId)
      .then((response) => {
        return response
      })
  },
  saveRevokeCertificateForm ({ commit }, requestData) {
    return certificateApi.putRevokeCertificateForm(requestData.data.certificateId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  saveSuspendCertificateForm ({ commit }, requestData) {
    return certificateApi.putSuspendCertificateForm(requestData.data.certificateId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  saveSuspensionUpdateForm ({ commit }, requestData) {
    return certificateApi.putSuspensionUpdateForm(requestData.data.certificateId, requestData.serializedData).then((response) => {
      return response
    }).catch((err) => {
      throw err
    })
  },
  exportCertificateFilterToXls ({ commit }, requestData) {
    return certificateApi.getCertificatesExcel(requestData.filter, requestData.sheetName).then((response) => {
      downloadXlsFromResponse(response, requestData.name)
    }).catch((err) => {
      throw err
    })
  },
  clearCertificateData ({ commit }) {
    commit('setStateCertificateFormDescriptors', [])
    commit('setStateCertificateFormDatas', [])
    commit('setStateCertificates', [])
  },
  startReviseCaseAction ({ commit }, certificateId) {
    return certificateApi.postStartReviseCase(certificateId)
      .then((response) => {
        return response
      })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
